import { createClient, SupabaseClient } from "@supabase/supabase-js";

// These should be set as environment variables in production
// For development, you can create a .env file (not committed to git)
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || "";
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || "";

let supabaseInstance: SupabaseClient | null = null;

/**
 * Unified storage adapter that uses localStorage to sync with AuthStorage
 * This ensures Supabase can access sessions stored by AuthStorage (which uses
 * Office.context.roamingSettings with localStorage fallback).
 *
 * Since Supabase's storage interface is synchronous but AuthStorage is async,
 * we use localStorage as the synchronous bridge. AuthStorage already writes
 * to localStorage as a fallback, so this keeps them in sync.
 */
// Helper functions for localStorage operations
const getFromLocalStorage = (key: string): string | null => {
  try {
    if (typeof localStorage === "undefined") {
      return null;
    }
    return localStorage.getItem(key);
  } catch (error) {
    console.error("Error reading from localStorage:", error);
    return null;
  }
};

const saveToLocalStorage = (key: string, value: string): void => {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(key, value);
    }
  } catch (error) {
    console.error("Error saving to localStorage:", error);
  }
};

const removeFromLocalStorage = (key: string): void => {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error("Error removing from localStorage:", error);
  }
};

const unifiedStorage = {
  getItem: (key: string) => {
    // Try Office.context.roamingSettings first (primary), fallback to localStorage
    if (typeof Office !== "undefined" && Office.context && Office.context.roamingSettings) {
      try {
        const value = Office.context.roamingSettings.get(key);
        if (value && typeof value === "string") {
          return value;
        }
      } catch (error) {
        console.error("Error reading from Office settings:", error);
      }
    }
    // Fallback to localStorage
    return getFromLocalStorage(key);
  },

  setItem: (key: string, value: string) => {
    // Save to Office.context.roamingSettings first (primary), then localStorage as backup
    if (typeof Office !== "undefined" && Office.context && Office.context.roamingSettings) {
      try {
        Office.context.roamingSettings.set(key, value);
        // Note: We don't await saveAsync here to keep it synchronous
        // The async save will happen in the background
        Office.context.roamingSettings.saveAsync(() => {
          // Silently handle async save - errors are non-critical
        });
      } catch (error) {
        console.error("Error saving to Office settings:", error);
      }
    }
    // Always save to localStorage as backup
    saveToLocalStorage(key, value);
  },

  removeItem: (key: string) => {
    // Remove from Office.context.roamingSettings first, then localStorage
    if (typeof Office !== "undefined" && Office.context && Office.context.roamingSettings) {
      try {
        Office.context.roamingSettings.remove(key);
        Office.context.roamingSettings.saveAsync(() => {
          // Silently handle async save
        });
      } catch (error) {
        console.error("Error removing from Office settings:", error);
      }
    }
    // Always remove from localStorage as well
    removeFromLocalStorage(key);
  },
};

export function getSupabaseClient(): SupabaseClient {
  if (!supabaseInstance) {
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error(
        "Supabase URL and Anon Key must be configured. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY environment variables."
      );
    }
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        storage: unifiedStorage,
        autoRefreshToken: true,
        persistSession: false, // Manual persistence via AuthStorage
        detectSessionInUrl: false, // OAuth handled via dialog, not URL
      },
    });
  }
  return supabaseInstance;
}

export { supabaseUrl, supabaseAnonKey };
